<template>
  <div class="inline-flex">
    <!-- Bell Button, with a badge for unread notifications + pending invites -->
    <OverlayBadge :value="totalBadgeCount" severity="danger" :pt="badgePt">
      <Button
        id="btn-notification-bell"
        severity="secondary"
        text
        rounded
        v-tooltip.bottom="$t('notifications.title')"
        :aria-label="$t('notifications.title')"
        :aria-expanded="isOpen"
        :aria-controls="isOpen ? 'notification-center' : undefined"
        @click="toggleNotificationCenter"
      >
        <template #icon>
          <span class="material-icons select-none" aria-hidden="true">notifications</span>
        </template>
      </Button>
    </OverlayBadge>

    <!-- Notification Center: a right-hand Drawer, rendered into <body> -->
    <NotificationCenter
      :is-open="isOpen"
      :notifications="notifications"
      :loading="loading"
      :unread-count="unreadCount"
      @close="closeNotificationCenter"
      @mark-as-read="handleMarkAsRead"
      @mark-all-read="handleMarkAllAsRead"
      @fetch="handleFetch"
    />
  </div>
</template>

<script setup>
/**
 * NotificationBell.vue
 *
 * Header bell (PrimeVue text, rounded Button with a Material `notifications` icon)
 * wrapped in an OverlayBadge that counts unread notifications plus pending workspace
 * invites; the badge is hidden when the count is zero. Clicking the bell loads the
 * first page of notifications and the invites, then opens the NotificationCenter
 * Drawer. Used by Header.vue and AdminHeader.vue; takes no props or events.
 *
 * @example
 * <NotificationBell />
 */
import { ref, computed, onMounted, nextTick } from 'vue';
import NotificationCenter from './NotificationCenter.vue';
import { useNotifications } from '../composables/useNotifications';
import { useWorkspaceStore } from '../stores/workspaceStore';

const {
  notifications,
  unreadCount,
  loading,
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
} = useNotifications();

const workspaceStore = useWorkspaceStore();

const isOpen = ref(false);

const totalBadgeCount = computed(
  () => unreadCount.value + workspaceStore.pendingInviteCount,
);

/**
 * Pass-through for the OverlayBadge's inner Badge. OverlayBadge always renders its
 * Badge (an empty value becomes a dot), so it is hidden inline when there is nothing
 * to count. A Tailwind `hidden` class would lose to PrimeVue's unlayered `.p-badge`.
 */
const badgePt = computed(() => ({
  pcBadge: {
    root: { style: totalBadgeCount.value > 0 ? undefined : { display: 'none' } },
  },
}));

const toggleNotificationCenter = () => {
  if (!isOpen.value) {
    // Carica notifiche e inviti quando apriamo il centro
    fetchNotifications(1, false);
    workspaceStore.loadInvites();
  }
  isOpen.value = !isOpen.value;
};

const closeNotificationCenter = () => {
  isOpen.value = false;
  // Drawer's focus trap does not restore focus on close: send it back to the bell.
  nextTick(() => document.getElementById('btn-notification-bell')?.focus());
};

const handleMarkAsRead = (notificationId) => {
  markAsRead(notificationId);
};

const handleMarkAllAsRead = () => {
  markAllAsRead();
};

const handleFetch = (page) => {
  fetchNotifications(page, false);
};

onMounted(() => {
  // Carica i contatori iniziali
  fetchUnreadCount();
  workspaceStore.loadInvites();
});
</script>
